import { useState, useEffect } from "react";
import { X, Check, Copy } from "lucide-react";
import { buildShareUrl, copyToClipboard } from "../../utils/url.js";

export default function ShareModal({ open, onClose, tripId, legs }) {
  const [selected, setSelected] = useState(() => new Set(legs.map((l) => l.id)));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set(legs.map((l) => l.id)));
      setCopied(false);
    }
  }, [open, legs]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIds = legs.map((l) => l.id).filter((id) => selected.has(id));
  const url = selectedIds.length > 0 && tripId ? buildShareUrl(tripId, selectedIds) : "";

  const copy = async () => {
    if (!url) return;
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const allOn = legs.length > 0 && legs.every((l) => selected.has(l.id));
  const toggleAll = () => {
    if (allOn) setSelected(new Set());
    else setSelected(new Set(legs.map((l) => l.id)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "92dvh" }}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Share trip
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Pick which legs to share</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>{selected.size} of {legs.length} selected</span>
            <button onClick={toggleAll} className="text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 font-medium underline-offset-4 hover:underline">
              {allOn ? "Deselect all" : "Select all"}
            </button>
          </div>

          <div className="space-y-1.5">
            {legs.map((leg) => {
              const on = selected.has(leg.id);
              return (
                <label
                  key={leg.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl border bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer transition-colors"
                  style={{ borderColor: on ? leg.colour : "#e2e8f0" }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(leg.id)}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <span className="w-2 h-6 rounded-full" style={{ background: leg.colour }} />
                  <span className="text-lg">{leg.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{leg.city}, {leg.country}</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{leg.start} → {leg.end}</div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="pt-2">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Share URL</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={url || "Select at least one leg…"}
                onClick={(e) => e.target.select()}
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-xs font-mono text-slate-700 dark:text-slate-300"
              />
              <button
                onClick={copy}
                disabled={!url}
                className="px-4 py-2 rounded-xl bg-slate-900 dark:bg-slate-600 text-white font-medium hover:bg-slate-700 dark:hover:bg-slate-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm flex items-center gap-1.5"
              >
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
              Anyone with this URL can view and edit the selected legs. They can't see other legs in this trip.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
